import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Typography,
  Tag,
  Popconfirm,
  message,
  Spin,
  Input,
  Row,
  Col,
  Statistic,
  Empty,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  CreditCardOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  BuildOutlined,
} from '@ant-design/icons';
import { SubscriptionService } from '../../services/api/subscriptionService';
import { CompanyService } from '../../services/api/companyService';
import {
  Subscription,
  SubscriptionFilters as SubscriptionFiltersType,
  SubscriptionCreate,
  CompanySubscription,
} from '../../types/subscription';
import { Company } from '../../types/company';
import { SubscriptionStatusBadge } from './SubscriptionStatusBadge';
import { SubscriptionFilters } from './SubscriptionFilters';
import { CreateSubscriptionDialog } from './CreateSubscriptionDialog';
import { SubscriptionDetail } from './SubscriptionDetail';

const { Title, Text } = Typography;
const { Search } = Input;

interface SubscriptionManagementProps {
  className?: string;
}

/**
 * Subscription management component for viewing and managing subscriptions
 * @feature PLATFORM_API_ACCESS - Enables subscription management through API access
 */
interface SubscriptionWithCompany extends Subscription {
  company?: Company;
}

export const SubscriptionManagement: React.FC<SubscriptionManagementProps> = ({ className }) => {
  const [subscriptions, setSubscriptions] = useState<SubscriptionWithCompany[]>([]);
  const [loading, setLoading] = useState(false);
  const [isCreateDialogVisible, setIsCreateDialogVisible] = useState(false);
  const [isDetailDrawerVisible, setIsDetailDrawerVisible] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null);
  const [searchText, setSearchText] = useState('');
  const [filters, setFilters] = useState<SubscriptionFiltersType>({});
  const [companyMap, setCompanyMap] = useState<Record<string, Company>>({});

  useEffect(() => {
    if (filters.company_id) {
      loadSubscriptions();
    } else {
      setSubscriptions([]);
    }
  }, [filters]);

  const loadSubscriptions = async () => {
    if (!filters.company_id) {
      setSubscriptions([]);
      return;
    }

    setLoading(true);
    try {
      // Load company information
      let company: Company | null = null;
      try {
        company = await CompanyService.getById(filters.company_id);
        if (company) {
          setCompanyMap({ [company.id]: company });
        }
      } catch (err) {
        console.error(`Failed to fetch company ${filters.company_id}:`, err);
      }

      // Get company subscriptions
      const companySubs = await SubscriptionService.getCompanySubscriptions(filters.company_id);
      
      // Fetch full subscription details for each company subscription
      const subs: SubscriptionWithCompany[] = [];
      for (const cs of companySubs) {
        if (!cs.subscription_id && !cs.id) {
          console.warn('Company subscription missing both subscription_id and id:', cs);
          continue;
        }

        try {
          // Try to get full subscription details using company subscription endpoint
          // The endpoint might accept either cs.id (company subscription ID) or cs.subscription_id (subscription ID)
          let sub: Subscription | null = null;
          
          // First try with subscription_id (the actual subscription ID)
          if (cs.subscription_id) {
            try {
              sub = await SubscriptionService.getCompanySubscriptionById(
                filters.company_id!,
                cs.subscription_id
              );
            } catch (err) {
              console.log(`Failed to fetch with subscription_id ${cs.subscription_id}, trying with company subscription ID ${cs.id}`);
              // If that fails, try with cs.id (company subscription join table ID)
              if (cs.id) {
                try {
                  sub = await SubscriptionService.getCompanySubscriptionById(
                    filters.company_id!,
                    cs.id
                  );
                } catch (err2) {
                  // Last resort: try generic subscription endpoint with context (ISSUE-110 fix)
                  if (cs.subscription_id && filters.company_id) {
                    sub = await SubscriptionService.getById(cs.subscription_id, { companyId: filters.company_id });
                  }
                }
              }
            }
          } else if (cs.id) {
            // If no subscription_id, try with company subscription ID
            try {
              sub = await SubscriptionService.getCompanySubscriptionById(
                filters.company_id!,
                cs.id
              );
            } catch (err) {
              console.error(`Failed to fetch subscription with company subscription ID ${cs.id}:`, err);
            }
          }

          // If subscription_type is missing or incomplete, try to fetch it separately
          if (sub && sub.subscription_type_id) {
            const needsSubscriptionType = !sub.subscription_type || !sub.subscription_type.name;
            if (needsSubscriptionType) {
              console.log(`Subscription ${sub.id} subscription_type missing or incomplete, fetching separately`);
              try {
                // ISSUE-110 fix: Provide context when calling getById
                // Determine context from subscription object or filters
                const context = sub.company_id 
                  ? { companyId: sub.company_id }
                  : sub.user_id
                  ? { userId: sub.user_id }
                  : filters.company_id
                  ? { companyId: filters.company_id }
                  : null;
                
                if (context) {
                  // Try scoped endpoint with context
                  const genericSub = await SubscriptionService.getById(sub.id, context);
                  if (genericSub && genericSub.subscription_type && genericSub.subscription_type.name) {
                    sub = { ...sub, subscription_type: genericSub.subscription_type };
                  } else {
                    // Fetch subscription type separately
                    const subscriptionType = await SubscriptionService.getSubscriptionTypeById(sub.subscription_type_id);
                    if (subscriptionType) {
                      sub = { ...sub, subscription_type: subscriptionType };
                    }
                  }
                } else {
                  // No context available, just fetch subscription type directly
                  const subscriptionType = await SubscriptionService.getSubscriptionTypeById(sub.subscription_type_id);
                  if (subscriptionType) {
                    sub = { ...sub, subscription_type: subscriptionType };
                  }
                }
              } catch (typeErr) {
                console.warn(`Failed to fetch subscription_type for subscription ${sub.id}:`, typeErr);
              }
            }
          }

          if (sub) {
            console.log(`Loaded subscription ${sub.id}:`, {
              hasSubscriptionType: !!sub.subscription_type,
              subscriptionTypeId: sub.subscription_type_id,
              subscriptionTypeName: sub.subscription_type?.name,
              subscriptionTypeObject: sub.subscription_type,
              subscriptionKeys: sub.subscription_type ? Object.keys(sub.subscription_type) : [],
            });
            subs.push({
              ...sub,
              company: company || undefined,
            });
          }
        } catch (err) {
          console.error(`Failed to fetch subscription for company subscription ${cs.id}:`, err);
        }
      }
      setSubscriptions(subs);
    } catch (err: any) {
      message.error(err.message || 'Failed to load subscriptions');
      setSubscriptions([]);
    } finally {
      setLoading(false);
    }
  };

  // ISSUE-058/066 fix: Memoize handlers to prevent unnecessary re-renders
  const handleCreateSubscription = useCallback(() => {
    setIsCreateDialogVisible(true);
  }, []);

  const handleViewSubscription = useCallback((subscription: Subscription) => {
    setSelectedSubscription(subscription);
    setIsDetailDrawerVisible(true);
  }, []);

  const handleEditSubscription = useCallback((subscription: Subscription) => {
    setSelectedSubscription(subscription);
    setIsDetailDrawerVisible(true);
  }, []);

  const handleDeleteSubscription = useCallback(async (subscriptionId: string) => {
    try {
      await SubscriptionService.delete(subscriptionId);
      message.success('Subscription cancelled successfully');
      await loadSubscriptions();
    } catch (err: any) {
      message.error(err.message || 'Failed to cancel subscription');
    }
  }, [filters.company_id]);

  const handleCreateSuccess = useCallback(() => {
    setIsCreateDialogVisible(false);
    loadSubscriptions();
  }, [filters.company_id]);

  const handleFilterChange = useCallback((newFilters: SubscriptionFiltersType) => {
    setFilters(newFilters);
  }, []);

  const handleFilterReset = useCallback(() => {
    setFilters({});
    setSearchText('');
  }, []);

  // ISSUE-058/066 fix: Memoize filtered subscriptions to avoid recalculation on every render
  const filteredSubscriptions = useMemo(() => subscriptions.filter((subscription) => {
    // Apply search filter
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      const matchesSearch =
        subscription.subscription_type?.name.toLowerCase().includes(searchLower) ||
        subscription.subscription_type?.code.toLowerCase().includes(searchLower) ||
        subscription.status.toLowerCase().includes(searchLower) ||
        subscription.id.toLowerCase().includes(searchLower) ||
        subscription.company?.name.toLowerCase().includes(searchLower);
      if (!matchesSearch) return false;
    }

    // Apply status filter
    if (filters.status) {
      if (subscription.status !== filters.status) return false;
    }

    // Apply subscription type filter
    if (filters.subscription_type_code) {
      if (subscription.subscription_type?.code !== filters.subscription_type_code) return false;
    }

    return true;
  }), [subscriptions, searchText, filters.status, filters.subscription_type_code]);

  // ISSUE-058/066 fix: Memoize columns to prevent re-renders
  const columns = useMemo(() => [
    {
      title: 'Company',
      key: 'company',
      render: (subscription: SubscriptionWithCompany) => (
        <Text strong>{subscription.company?.name || 'Unknown Company'}</Text>
      ),
      sorter: (a: SubscriptionWithCompany, b: SubscriptionWithCompany) =>
        (a.company?.name || '').localeCompare(b.company?.name || ''),
    },
    {
      title: 'Subscription',
      key: 'subscription',
      render: (subscription: SubscriptionWithCompany) => (
        <Button
          type="link"
          onClick={() => handleViewSubscription(subscription)}
          style={{ padding: 0 }}
        >
          <Space>
            <CreditCardOutlined />
            <Text strong>
              {subscription.subscription_type?.name || 'Unknown Subscription'}
            </Text>
          </Space>
        </Button>
      ),
      sorter: (a: SubscriptionWithCompany, b: SubscriptionWithCompany) =>
        (a.subscription_type?.name || '').localeCompare(b.subscription_type?.name || ''),
    },
    {
      title: 'Type',
      key: 'type',
      render: (subscription: SubscriptionWithCompany) => (
        <Tag>{subscription.subscription_type?.code || 'N/A'}</Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => <SubscriptionStatusBadge status={status as any} />,
      sorter: (a: SubscriptionWithCompany, b: SubscriptionWithCompany) => a.status.localeCompare(b.status),
    },
    {
      title: 'Users',
      key: 'users',
      render: (subscription: SubscriptionWithCompany) => {
        const userCount = subscription.assigned_users?.length || 0;
        return userCount > 0 ? (
          <Tag icon={<TeamOutlined />}>{userCount}</Tag>
        ) : (
          <Text type="secondary">None</Text>
        );
      },
    },
    {
      title: 'Features',
      key: 'features',
      render: (subscription: Subscription) => {
        const featureCount =
          subscription.features?.length || subscription.subscription_type?.features?.length || 0;
        return featureCount > 0 ? (
          <Tag icon={<CheckCircleOutlined />}>{featureCount}</Tag>
        ) : (
          <Text type="secondary">None</Text>
        );
      },
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => (
        <Text type="secondary">{new Date(date).toLocaleDateString()}</Text>
      ),
      sorter: (a: Subscription, b: Subscription) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (subscription: SubscriptionWithCompany) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEditSubscription(subscription)}
            title="Edit subscription"
          />
          {subscription.status !== 'cancelled' && (
            <Popconfirm
              title="Are you sure you want to cancel this subscription?"
              description="This action cannot be undone."
              onConfirm={() => handleDeleteSubscription(subscription.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                title="Cancel subscription"
              />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ], [handleViewSubscription, handleEditSubscription, handleDeleteSubscription]);

  // ISSUE-058/066 fix: Memoize computed statistics
  const { totalActive, totalPending, totalUsers } = useMemo(() => ({
    totalActive: subscriptions.filter((s) => s.status === 'active').length,
    totalPending: subscriptions.filter((s) => s.status === 'pending').length,
    totalUsers: subscriptions.reduce(
      (sum, s) => sum + (s.assigned_users?.length || 0),
      0
    ),
  }), [subscriptions]);

  return (
    <div className={className}>
      <Card>
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={24}>
            <Title level={2}>Subscription Management</Title>
          </Col>
        </Row>

        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={8}>
            <Statistic
              title="Total Subscriptions"
              value={subscriptions.length}
              prefix={<CreditCardOutlined />}
            />
          </Col>
          <Col xs={24} sm={8}>
            <Statistic
              title="Active Subscriptions"
              value={totalActive}
              valueStyle={{ color: '#3f8600' }}
            />
          </Col>
          <Col xs={24} sm={8}>
            <Statistic
              title="Total Assigned Users"
              value={totalUsers}
              prefix={<TeamOutlined />}
            />
          </Col>
        </Row>

        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12}>
            <Search
              placeholder="Search subscriptions..."
              allowClear
              enterButton={<SearchOutlined />}
              size="large"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ marginBottom: 16 }}
            />
          </Col>
          <Col xs={24} sm={12} style={{ textAlign: 'right' }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              size="large"
              onClick={handleCreateSubscription}
            >
              Create Subscription
            </Button>
          </Col>
        </Row>

        <SubscriptionFilters
          filters={filters}
          onChange={handleFilterChange}
          onReset={handleFilterReset}
        />

        {!filters.company_id ? (
          <Empty
            description={
              <div>
                <Text type="secondary" style={{ fontSize: '16px' }}>
                  Please select a company to view subscriptions
                </Text>
                <br />
                <Text type="secondary" style={{ fontSize: '14px', marginTop: '8px', display: 'block' }}>
                  Use the Company filter above to see subscriptions for a specific company.
                </Text>
              </div>
            }
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ padding: '48px 0' }}
          />
        ) : (
          <Table
            columns={columns}
            dataSource={filteredSubscriptions}
            rowKey="id"
            loading={loading}
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} subscriptions`,
            }}
            locale={{
              emptyText: 'No subscriptions found for this company',
            }}
          />
        )}
      </Card>

      <CreateSubscriptionDialog
        visible={isCreateDialogVisible}
        onCancel={() => setIsCreateDialogVisible(false)}
        onSuccess={handleCreateSuccess}
      />

      {selectedSubscription && (
        <SubscriptionDetail
          visible={isDetailDrawerVisible}
          subscription={selectedSubscription}
          onClose={() => {
            setIsDetailDrawerVisible(false);
            setSelectedSubscription(null);
          }}
          onUpdate={loadSubscriptions}
        />
      )}
    </div>
  );
};

